import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { DataViewer } from '../../abstract/DataViewer';
import { AgentWorkspaceContext } from '../interfaces/agent-session';
import { AgentWorkspaceRegistry } from './agent-workspace-registry.service';
import { canvasSnapshot } from '../live-canvas-snapshot';
import { flattenShapes } from '../live-canvas-snapshot';
import { canvasHash } from '../canvas-hash';
import { LiveCreatorInvocationContext, LiveCreatorCommitEnvelope, AgentMutationJournal } from '../../interfaces/live-creator.contract';
import { BeNeo4jService } from '../../services/be-neo4j.service';
import { DBItem } from '../../interfaces/Record';
import { extractElements, separateNodesFromRelations } from '../../utils/RecordUtils';
import { applyCanvasPatch, parseOperations, PatchSemantics } from '../live-canvas-patch';
import { isRecord } from './agent-stream';

export interface LiveRequest {
  owner: DataViewer;
  context: LiveCreatorInvocationContext;
  hash: string;
  applied: boolean;
}
export class LiveConflict extends Error {
  constructor(public readonly commit: LiveCreatorCommitEnvelope, public readonly reason: 'CANVAS_REVISION_CONFLICT' | 'CANVAS_APPLY_FAILED',
              public readonly safeDetail?: string) { super(reason); }
}

@Injectable({ providedIn: 'root' })
export class LiveCreatorAdapter {
  private readonly registry = inject(AgentWorkspaceRegistry);
  private readonly backend = inject(BeNeo4jService);

  async capture(scope: AgentWorkspaceContext, requestId: string,
                prior?: AgentMutationJournal, rebase?: LiveCreatorInvocationContext['rebase'],
                layoutValidationRequired = false): Promise<LiveRequest> {
    const owner = this.registry.instance(scope) as DataViewer | null;
    // Live Canvas capability belongs to a MODEL_CREATOR independently from a
    // structural target. Mutation methods retain their own target checks.
    if (!owner || owner.agentGestureActive) throw new Error('Live Creator Canvas unavailable');
    owner.observeAgentCanvas();
    const state = owner.agentCanvasState;
    // Snapshot the complete live Canvas for inspection and revision hashing.
    // It must not collapse to the selected Model in a multi-Model Creator.
    const roots = owner.canvasItems;
    const canvas = this.protocolCanvas(roots, owner.connections);
    // The Canvas snapshot deliberately contains every imported Model. Keep a
    // separate target-scoped membership list so an identical/stale visual
    // instance in another imported Model cannot suppress NODE_ADD here.
    const targetRoots = scope.modelUuid && scope.hierarchy ? this.roots(owner, scope) : [];
    const targetElementDbIds = [...new Set(flattenShapes(targetRoots)
      .map(item => item.dbPropierties?.__EDT_UUID__)
      .filter((id): id is string => typeof id === 'string' && id.length > 0))];
    const context: LiveCreatorInvocationContext = { active: true, protocol_version: 1,
      request_id: requestId, tab_instance_id: scope.tabInstanceId, view_uuid: scope.viewUuid,
      view_name: scope.viewName, model_uuid: scope.modelUuid ?? null, hierarchy: scope.hierarchy ?? null,
      represented_models: (scope.representedModels ?? []).map(model => ({ model_uuid: model.modelUuid,
        hierarchy: model.hierarchy, ...(model.name ? { name: model.name } : {}) })),
      target_element_db_ids: targetElementDbIds,
      canvas_revision: state.revision, saved_revision: state.savedRevision,
      dirty: state.revision !== state.savedRevision, canvas,
      layout_validation_required: layoutValidationRequired,
      prior_agent_mutation_journal: prior ?? null, rebase: rebase ?? null };
    const hash = await canvasHash(canvas);
    owner.observeAgentCanvas();
    if (!this.registry.isCurrent(scope) || owner.agentGestureActive || state.revision !== context.canvas_revision) throw new Error('Canvas changed while preparing the request');
    context.event_batch = state.journal.beginBatch(state.acknowledgedRevision, state.revision, () => crypto.randomUUID());
    return { owner, context, hash, applied: false };
  }

  async loadCreatedModel(scope: AgentWorkspaceContext, raw: unknown, stillActive: () => boolean): Promise<void> {
    if (scope.modelUuid || scope.modelTargetState === 'ambiguous' || !isRecord(raw)
        || typeof raw['model_uuid'] !== 'string' || !raw['model_uuid'] || typeof raw['hierarchy'] !== 'string' || !raw['hierarchy']
        || typeof raw['model_name'] !== 'string' || !raw['model_name']) throw new Error('Invalid created Model');
    const owner = this.registry.instance(scope) as DataViewer | null;
    if (!owner) throw new Error('Live Creator Canvas unavailable');
    // Some existing Creator flows load an imported Model before the agent's
    // continuation observes the final response. Reuse that Canvas only when
    // its canonical View group proves it is the exact Model just created.
    // Any other non-empty Canvas remains protected from an implicit replace.
    const isCreatedModelGroup = (view: { model_UUID?: string; hierarchy?: string }) =>
      view.model_UUID === raw['model_uuid'] && view.hierarchy === raw['hierarchy'];
    // The Creator may retain a blank, anonymous ViewsCanvas while it has no
    // visible Canvas items. It is an initialization artefact, not a View the
    // user could have edited, and must not prevent a just-imported Model from
    // being loaded. Do not generalize this to named/populated View groups.
    const isEmptyAnonymousPlaceholder = (view: {
      model_UUID?: string; hierarchy?: string; view?: readonly unknown[]; conections?: readonly unknown[];
    }) => !view.model_UUID && !view.hierarchy
      && (!view.view || view.view.length === 0)
      && (!view.conections || view.conections.length === 0);
    // Deleting the last/root Canvas node removes every visible descendant but
    // legacy ViewsCanvas groups and Connection objects can retain references to
    // those removed shapes. With no live Canvas items no connection can be
    // valid user content, so discard both stale projections and reuse this
    // Creator exactly like a genuinely blank tab.
    if (!owner.canvasItems.length) {
      owner.connections = [];
      owner.viewCanvas = [];
    }
    const hasCreatedModelGroup = owner.viewCanvas.some(isCreatedModelGroup);
    const hasUnrelatedViewGroup = owner.viewCanvas.some(view =>
      !isCreatedModelGroup(view) && !isEmptyAnonymousPlaceholder(view));
    if (owner.canvasItems.length > 0 && hasCreatedModelGroup) {
      owner.observeAgentCanvas();
      return;
    }
    if (owner.canvasItems.length || hasUnrelatedViewGroup) throw new Error('Creator contains an existing Canvas');
    // Empty groups for the exact Model and anonymous empty groups are transient
    // placeholders created by the normal Creator/import path. They contain no
    // user Canvas content, so clear them before the authoritative Model read.
    if (hasCreatedModelGroup || owner.viewCanvas.some(isEmptyAnonymousPlaceholder)) owner.viewCanvas = [];
    const connection = owner.savedDocument.connection.dataService.string_connection;
    const query = await firstValueFrom(this.backend.getCypherById(raw['model_uuid'], raw['hierarchy'], true, connection));
    if (!isRecord(query) || typeof query['result'] !== 'string') throw new Error('Invalid Model read response');
    const result = await firstValueFrom(this.backend.Execute(query['result'], connection));
    const createdGroupDuringRead = owner.viewCanvas.some(isCreatedModelGroup);
    const unrelatedGroupDuringRead = owner.viewCanvas.some(view =>
      !isCreatedModelGroup(view) && !isEmptyAnonymousPlaceholder(view));
    if (owner.canvasItems.length > 0 && createdGroupDuringRead) {
      owner.observeAgentCanvas();
      return;
    }
    if (!stillActive() || !this.registry.isCurrent(scope) || this.registry.instance(scope) !== owner
        || owner.canvasItems.length || unrelatedGroupDuringRead || owner.agentGestureActive) throw new Error('Creator changed before loading');
    if (createdGroupDuringRead || owner.viewCanvas.some(isEmptyAnonymousPlaceholder)) owner.viewCanvas = [];
    const previousDocument = { ...owner.savedDocument };
    const previousImport = owner.importDocument;
    owner.agentApplying = true;
    try {
      owner.savedDocument.model_UUID = raw['model_uuid']; owner.savedDocument.hierarchy = raw['hierarchy']; owner.savedDocument.name_value = raw['model_name'];
      owner.processExecution(result, query['result']);
      if (!owner.canvasItems.length) throw new Error('The created Model returned no Canvas elements');
      for (const view of owner.viewCanvas) { view.model_UUID = raw['model_uuid']; view.hierarchy = raw['hierarchy']; }
      owner.draw();
      owner.agentCanvasState.committed(canvasSnapshot(owner.canvasItems, owner.connections));
      owner.agentContextRevision++;
    } catch (error) {
      owner.canvasItems = []; owner.connections = []; owner.viewCanvas = [];
      owner.savedDocument = previousDocument; owner.importDocument = previousImport;
      throw error;
    } finally { owner.agentApplying = false; }
  }

  /** Apply the generic initial layout prepared and validated in the same
   * Runtime invocation that imported the Model. This preserves the normal
   * revision, patch, open-container and camera-centering path without starting
   * a second LLM request. */
  async applyCreatedModelInitialLayout(scope: AgentWorkspaceContext, requestId: string,
      raw: unknown, stillActive: () => boolean): Promise<void> {
    if (!isRecord(raw) || raw['profile'] !== 'generic'
        || typeof raw['validation_id'] !== 'string' || !raw['validation_id']
        || typeof raw['operations_sha256'] !== 'string' || !/^[a-f0-9]{64}$/.test(raw['operations_sha256'])) {
      throw new Error('Invalid created Model initial layout');
    }
    const operations = parseOperations(raw['operations']);
    if (await canvasHash(operations) !== raw['operations_sha256']) {
      throw new Error('Created Model initial layout hash mismatch');
    }
    const request = await this.capture(scope, requestId, undefined, undefined, true);
    const batch = request.context.event_batch;
    const journal: AgentMutationJournal = { modelNodesAdded: [], modelNodesRemoved: [], modelFlowsAdded: [],
      modelFlowsRemoved: [], modelNodesRenamed: [], modelNodeShapesChanged: [], modelNodesReparented: [] };
    const commit: LiveCreatorCommitEnvelope = {
      type: 'DIGITAL_TWIN_LIVE_CREATOR_COMMIT', protocol_version: 1,
      request_id: request.context.request_id,
      base_canvas_revision: request.context.canvas_revision,
      base_canvas_snapshot_sha256: request.hash,
      canvas_patch: {
        commit_id: crypto.randomUUID(),
        expected_canvas_revision: request.context.canvas_revision,
        canvas_snapshot_sha256: request.hash,
        operations,
        layout_validation: {
          validation_id: raw['validation_id'], layout_profile: 'generic',
          expected_canvas_revision: request.context.canvas_revision,
          operations_sha256: raw['operations_sha256']
        }
      },
      save_request: null,
      persisted_view_updated_by_runtime: false,
      frontend_revision_check_required: true,
      rebase_required_on_revision_conflict: true,
      prior_agent_mutation_journal: null,
      event_batch_ack_candidate: batch ? { batch_id: batch.batch_id,
        from_revision: batch.from_revision, to_revision: batch.to_revision } : null,
      agent_mutation_journal: journal
    };
    try {
      const applied = await this.apply(scope, request, commit, stillActive);
      this.acknowledge(request, applied);
    } catch (error) {
      if (!request.applied) this.release(request);
      throw error;
    }
  }

  release(request: LiveRequest): void {
    if (request.context.event_batch) request.owner.agentCanvasState.journal.nack(request.context.event_batch.batch_id);
  }

  async apply(scope: AgentWorkspaceContext, request: LiveRequest, raw: unknown, stillActive: () => boolean): Promise<LiveCreatorCommitEnvelope> {
    if (!isRecord(raw) || raw['type'] !== 'DIGITAL_TWIN_LIVE_CREATOR_COMMIT' || raw['protocol_version'] !== 1
        || raw['request_id'] !== request.context.request_id || raw['base_canvas_revision'] !== request.context.canvas_revision
        || raw['base_canvas_snapshot_sha256'] !== request.hash || raw['persisted_view_updated_by_runtime'] !== false) throw new Error('Invalid live commit');
    const commit = raw as unknown as LiveCreatorCommitEnvelope;
    const resolvedTarget = commit.resolved_target;
    if (resolvedTarget != null && (!isRecord(resolvedTarget)
        || typeof resolvedTarget['model_uuid'] !== 'string' || !resolvedTarget['model_uuid']
        || typeof resolvedTarget['hierarchy'] !== 'string' || !resolvedTarget['hierarchy']
        || !(scope.representedModels ?? []).some(model => model.modelUuid === resolvedTarget['model_uuid']
          && model.hierarchy === resolvedTarget['hierarchy']))) throw new Error('Invalid resolved Canvas target');
    const effectiveScope = resolvedTarget
      ? { ...scope, modelUuid: resolvedTarget.model_uuid, hierarchy: resolvedTarget.hierarchy,
        modelTargetState: 'resolved' as const }
      : scope;
    const journal = commit.agent_mutation_journal;
    const journalFields = ['modelNodesAdded', 'modelNodesRemoved', 'modelFlowsAdded', 'modelFlowsRemoved', 'modelNodesRenamed', 'modelNodeShapesChanged', 'modelNodesReparented'];
    if (!isRecord(journal) || journalFields.some(key => !Array.isArray(journal[key])) || Object.keys(journal).some(key => !journalFields.includes(key))) throw new Error('Invalid mutation journal');
    const batch = request.context.event_batch;
    const ack = commit.event_batch_ack_candidate;
    if (batch ? !ack || ack.batch_id !== batch.batch_id || ack.from_revision !== batch.from_revision || ack.to_revision !== batch.to_revision : ack != null) throw new Error('Invalid batch ACK');
    const patch = commit.canvas_patch;
    if (patch && (!isRecord(patch) || typeof patch.commit_id !== 'string' || !patch.commit_id
        || patch.expected_canvas_revision !== request.context.canvas_revision || patch.canvas_snapshot_sha256 !== request.hash)) throw new Error('Invalid patch identity');
    const layoutValidation = patch?.layout_validation;
    if (request.context.layout_validation_required && !layoutValidation) {
      throw new Error('Required layout validation receipt missing');
    }
    if (layoutValidation && (!isRecord(layoutValidation)
        || typeof layoutValidation.validation_id !== 'string' || !layoutValidation.validation_id
        || !['generic', 'process_table'].includes(layoutValidation.layout_profile as string)
        || layoutValidation.expected_canvas_revision !== request.context.canvas_revision
        || typeof layoutValidation.operations_sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(layoutValidation.operations_sha256))) {
      throw new Error('Invalid layout validation receipt');
    }
    const operations = patch ? parseOperations(patch.operations) : [];
    if (commit.save_request && (typeof commit.save_request.request_id !== 'string' || !commit.save_request.request_id
        || commit.save_request.expected_canvas_revision !== request.context.canvas_revision
        || commit.save_request.use_existing_frontend_serializer !== true)) throw new Error('Invalid save request');
    // A live snapshot is attached to every non-empty Creator request so that
    // read-only questions see the real Canvas. With neither a patch nor a
    // save there is nothing to apply, however: do not revision-check or
    // rebase an inspection simply because the user kept interacting with the
    // Canvas while the agent was answering.
    if (!patch && !commit.save_request) return commit;
    this.check(scope, request, stillActive, commit);
    const semantics = await this.hydrate(effectiveScope, request.owner, operations);
    this.check(scope, request, stillActive, commit);
    if (patch) {
      const owner = request.owner;
      const targetRoots = this.roots(owner, effectiveScope);
      const previousImport = owner.importDocument;
      const previousIdentity = owner.savedDocument && {
        model_UUID: owner.savedDocument.model_UUID, hierarchy: owner.savedDocument.hierarchy
      };
      owner.agentApplying = true;
      try {
        applyCanvasPatch(owner, targetRoots, operations, semantics, () => {
          if (request.context.layout_validation_required) {
            // Initial generated Views must reveal their hierarchy immediately.
            // This is a presentation invariant, not an LLM style guess.
            for (const item of flattenShapes(targetRoots)) {
              if (item.children.length) item.setFill(false);
            }
            owner.fitBoxesWithCamera();
          } else {
            owner.draw();
          }
        });
        request.applied = true;
        owner.agentCanvasState.committed(canvasSnapshot(owner.canvasItems, owner.connections));
      } catch (error) {
        if (!request.applied) {
          owner.importDocument = previousImport;
          if (previousIdentity) Object.assign(owner.savedDocument, previousIdentity);
          throw new LiveConflict(commit, 'CANVAS_APPLY_FAILED', this.safeApplyDetail(error));
        }
        throw error;
      } finally { owner.agentApplying = false; }
    }
    return commit;
  }

  acknowledge(request: LiveRequest, commit: LiveCreatorCommitEnvelope): void {
    const batch = request.context.event_batch;
    if (!batch) { if (commit.event_batch_ack_candidate != null) throw new Error('Unexpected batch ACK'); return; }
    const ack = commit.event_batch_ack_candidate;
    if (!ack || ack.batch_id !== batch.batch_id || ack.from_revision !== batch.from_revision || ack.to_revision !== batch.to_revision) throw new Error('Invalid batch ACK');
    if (!request.owner.agentCanvasState.journal.ack(batch.batch_id)) throw new Error('Batch no longer in flight');
    request.owner.agentCanvasState.acknowledgedRevision = batch.to_revision;
  }

  private roots(owner: DataViewer, scope: AgentWorkspaceContext) {
    if (!owner.viewCanvas.length) return owner.canvasItems;
    const ids = new Set(owner.viewCanvas.filter(view => view.model_UUID === scope.modelUuid && view.hierarchy === scope.hierarchy)
      .flatMap(view => view.view.map(item => item.id)));
    const roots = owner.canvasItems.filter(item => ids.has(item.id));
    if (!roots.length) throw new Error('Model Canvas ownership cannot be established');
    return roots;
  }

  /**
   * The protocol addresses canonical nodes/flows by dbId/relation, whereas a
   * Creator may show duplicate visual instances after importing the same View
   * twice. Send one deterministic representative for each canonical identity;
   * the real Canvas is neither altered nor hidden from its own revision guard.
   */
  private protocolCanvas(roots: DataViewer['canvasItems'], connections: DataViewer['connections']) {
    const snapshot = canvasSnapshot(roots, connections);
    const seenNodes = new Set<string>(), seenConnections = new Set<string>();
    return {
      ...snapshot,
      elements: snapshot.elements.filter(element => {
        if (seenNodes.has(element.dbId)) return false;
        seenNodes.add(element.dbId); return true;
      }),
      connections: snapshot.connections.filter(connection => {
        if (seenConnections.has(connection.relation)) return false;
        seenConnections.add(connection.relation); return true;
      })
    };
  }

  /** Do not pass arbitrary renderer or backend exception text into the chat. */
  private safeApplyDetail(error: unknown): string | undefined {
    if (!(error instanceof Error)) return undefined;
    const known = new Set([
      'Node already visible', 'Node outside captured Model', 'Authoritative node data missing',
      'More than one Canvas instance represents this node', 'Cyclic parent'
    ]);
    return known.has(error.message) ? error.message : undefined;
  }

  private check(scope: AgentWorkspaceContext, request: LiveRequest, stillActive: () => boolean, commit: LiveCreatorCommitEnvelope): void {
    if (!stillActive() || !this.registry.isCurrent(scope) || this.registry.instance(scope) !== request.owner) throw new Error('Workspace replaced or request cancelled');
    request.owner.observeAgentCanvas();
    if (request.owner.agentGestureActive || request.owner.agentCanvasState.revision !== request.context.canvas_revision) throw new LiveConflict(commit, 'CANVAS_REVISION_CONFLICT');
  }

  private async hydrate(scope: AgentWorkspaceContext, owner: DataViewer, operations: Readonly<ReturnType<typeof parseOperations>>): Promise<PatchSemantics> {
    const nodes: DBItem[] = [], relations: DBItem[] = [];
    const needsSemantics = operations.some(op => op.type === 'NODE_ADD' || op.type === 'NODE_REFRESH_SEMANTICS' || op.type === 'CONNECTION_ADD');
    if (needsSemantics) {
      const connection = owner.savedDocument.connection.dataService.string_connection;
      const loadSubtree = async (nodeId: string): Promise<void> => {
        const query = await firstValueFrom(this.backend.getCypherById(nodeId, scope.hierarchy!, true, connection));
        if (!isRecord(query) || typeof query['result'] !== 'string') throw new Error('Invalid Model read response');
        const result = await firstValueFrom(this.backend.Execute(query['result'], connection));
        separateNodesFromRelations(extractElements(result.result), nodes, relations);
      };
      await loadSubtree(scope.modelUuid!);
      // A saved View's outer Model root can be a narrow representation. For a
      // freshly created node, its validated parent is the authoritative
      // fallback subtree and guarantees the node's DB data is available to the
      // local Canvas item constructor.
      const loadedIds = new Set(nodes.map(item => item.properties.__EDT_UUID__));
      const missingParents = [...new Set(operations
        .filter((op): op is Extract<typeof op, { type: 'NODE_ADD' }> => op.type === 'NODE_ADD')
        .filter(op => !loadedIds.has(op.dbId))
        .map(op => op.parentDbId))];
      for (const parentDbId of missingParents) await loadSubtree(parentDbId);
    }
    return { nodes: new Map(nodes.map(item => [item.properties.__EDT_UUID__, item])),
      relations: new Map(relations.map(item => [item.properties.__EDT_UUID__, item])) };
  }
}
